import * as Sentry from "@sentry/vue";
import { PromiseQueue } from "./promiseQueue.js";
// Inlined: the China index serves this bundle from the CN CDN, and the Worker constructor refuses
// a cross-origin script. Do not swap back to `?worker` or `?worker&url`.
import FrameUploadWorker from "./frameUploadWorker.js?worker&inline";

class DataLogger {
  static experience = "7f19f337-3d35-4e13-b6c7-d1b1b4d088f0";
  static authToken = "a5acdb33f998ec836a4c736aaf72405d9e636f1c";
  static bufferLimit = 60;

  constructor(sessionId, willUploadFrames = false) {
    this.sessionId = sessionId;
    this.attemptId = -1;

    this.willUploadFrames = willUploadFrames;
    this.initAttemptVariables();

    this.logPromise = null;

    this.frameData = {
      frameId: null,
      selectedCameraIndex: null,
      isTorchEnabled: null,
      blurScore: null,
      blurThreshold: null,
      glareScore: null,
      glareThreshold: null,
      imageSize: null,
      carrierPatternCenters: null,
      secureGraphicROI: null,
      sizeResult: null,
      sizeLowerLimit: null,
      sizeUpperLimit: null,
      codeContent: null,
      codeState: null,
      messageToUser: null
    };
  }

  initAttemptVariables() {
    if (this.willUploadFrames) {
      try {
        this.frameUploadWorker = new FrameUploadWorker();
        // TODO: report worker errors to Sentry. Needs addEventListener rather than onerror,
        // which uploadFrame() reassigns per frame, and extra fields rather than a cause,
        // which Sentry drops for a non-Error ErrorEvent.
        this.frameUploadWorker.onerror = (error) => {
          console.error("Frame Upload worker init error:\n", error);
        };
        this.inputFramesQueue = new PromiseQueue(DataLogger.bufferLimit);
        this.attemptedFetchFrameUploadData = false;
        this.frameUploadData = null;
      } catch (error) {
        Sentry.captureException(new Error("Frame upload worker could not be created", { cause: error }));
        this.willUploadFrames = false;
      }
    }
    this.inputFrameId = 0;
    this.timeToStartBenchmark = null;
    this.timeToFirstReadableFrame = null;
    this.timeToAuthCandidate = null;
    this.timeToResponseReceived = null;
    this.authTimeStamp = null;
    this.scanningCodeContent = null;
    this.scannedFramesData = [];
    this.inputFramesSendTimes = [];
    this.inputFramesProcessingTimes = [];
    this.inputFramesURLs = [];
    this.scannedFrameIDCounter = 0;
  }

  renewAttempt() {
    // Variable that don't need to be same across attempts.
    this.initAttemptVariables();

    this.resetFrameData();
  }

  getSessionId() {
    return this.sessionId;
  }

  getAttemptId() {
    return this.attemptId;
  }

  getInputFrameId() {
    return this.inputFrameId;
  }

  incrementInputFrameId() {
    this.inputFrameId += 1;
  }

  incrementAttemptId() {
    this.attemptId += 1;
    this.renewAttempt();
  }

  getDataLoggerExperience() {
    return DataLogger.experience;
  }

  getAuthToken() {
    return DataLogger.authToken;
  }

  recordTimeStartBenchmark(renew = false) {
    if (renew || !this.timeToStartBenchmark) {
      this.timeToStartBenchmark = performance.now();
    }
  }

  recordTimeFirstReadableFrame(renew = false) {
    if (renew || !this.timeToFirstReadableFrame) {
      this.timeToFirstReadableFrame = performance.now();
    }
  }

  recordTimeAuthCandidate(renew = false) {
    if (renew || !this.timeToAuthCandidate) {
      this.timeToAuthCandidate = performance.now();
    }
  }

  recordTimeResponseReceived(renew = false) {
    if (renew || !this.timeToResponseReceived) {
      this.timeToResponseReceived = performance.now();
    }
  }

  getTimeStartToRead() {
    return this.timeToFirstReadableFrame - this.timeToStartBenchmark;
  }

  getTimeReadToAuthCandidate() {
    return this.timeToAuthCandidate - this.timeToFirstReadableFrame;
  }

  getTimeAuthCandidateToResponseReceived() {
    return this.timeToResponseReceived - this.timeToAuthCandidate;
  }

  addFrameData() {
    const frameWithId = {
      ...this.frameData,
      id: this.scannedFrameIDCounter++
    };
    this.scannedFramesData.push(frameWithId);
    this.resetFrameData();
  }

  resetFrameData() {
    for (let property in this.frameData) {
      if (property === "messageToUser") {
        continue;
      }
      this.frameData[property] = null;
    }
  }

  processSDKTime() {
    for (let i = 0; i < this.inputFramesSendTimes.length - 1; i++) {
      let t = this.inputFramesSendTimes[i + 1] - this.inputFramesSendTimes[i];
      this.inputFramesProcessingTimes.push(t);
    }
  }

  async logScanData(
    uuid,
    deviceModel,
    deviceConfigured,
    browser,
    browserVersion,
    browserEngine,
    scanningURL,
    appVersion,
    availableCamerasIds,
    selectedCameraCapabilities,
    videoTrackSettings,
    authResult,
    frameId,
    authFailureMode,
    endSessionMode
  ) {
    this.processSDKTime();

    const experience = this.getDataLoggerExperience();
    const token = this.getAuthToken();

    const sessionId = this.getSessionId();

    const dataToSend = {
      experience: experience,
      key: {
        uuid: uuid,
        hasFrameImages: this.willUploadFrames && this.inputFramesURLs.length > 0,
        deviceModel: deviceModel,
        browser: browser,
        sessionId: sessionId
      },
      data: {
        deviceModel: deviceModel,
        browser: browser,
        browserVersion: browserVersion,
        browserEngine: browserEngine,
        scanningURL: scanningURL,
        appVersion: appVersion,
        availableCamerasIds: availableCamerasIds, // availableCameras.value,
        selectedCameraCapabilities: selectedCameraCapabilities, // authenticator.getCamera().getCapabilities(),
        videoTrackSettings: videoTrackSettings, //authenticator.getCamera().getVideoTrackSettings(),
        timeStartToRead: this.getTimeStartToRead(),
        timeReadToAuthCandidate: this.getTimeReadToAuthCandidate(),
        timeAuthCandidateToResponseReceived: this.getTimeAuthCandidateToResponseReceived(),
        authTimeStamp: this.authTimeStamp,
        authResult: authResult,
        sentFrameId: frameId,
        authFailureMode: authFailureMode,
        framesInformation: this.scannedFramesData,
        endSessionMode: endSessionMode,
        sessionId: sessionId,
        attemptId: this.getAttemptId(),
        deviceConfigured: deviceConfigured,
        uploadInputFrames: this.willUploadFrames, // This is for the branch where frames are logged.
        inputFramesProcessingTimes: this.inputFramesProcessingTimes,
        inputFramesURLs: this.inputFramesURLs,
        inputFramesBufferLimit: DataLogger.bufferLimit
      }
    };

    const apiHost = "rnd.scantrust.io";
    this.logPromise = fetch(`https://${apiHost}/api/data-logger/log/`, {
      credentials: "same-origin",
      mode: "cors",
      method: "post",
      headers: { "Content-Type": "application/json", Authorization: `Token ${token}` },
      body: JSON.stringify(dataToSend)
    })
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        } else {
          console.error("Status: " + resp.status);
          return Promise.reject(new Error("server"));
        }
      })
      .then((dataJson) => {
        console.log("Logged sucessfully");
      })
      .catch((err) => {
        console.error("Logging error: " + err);
        if (err === "server") return;
      });
    return this.logPromise;
  }

  async fetchFrameUploadData() {
    const experience = this.getDataLoggerExperience();
    const endpoint = `https://rnd.scantrust.io/api/data-logger/upload/${experience}/`;
    this.attemptedFetchFrameUploadData = true;

    try {
      const response = await fetch(endpoint);
      if (!response.ok) {
        console.log("Frame upload data status: " + response.status);
        return;
      }
      const data = await response.json();
      this.frameUploadData = data;
    } catch (err) {
      console.log("Frame upload data error: " + err);
    }
  }

  pushToFramesQueue(rgba, width, height) {
    const id = this.inputFrameId; // create a copy of the id as the inputFramesQueue needs to have the id of this frame.
    const attempt = this.attemptId;
    const localThis = this;
    this.inputFramesQueue
      .add(() => localThis.uploadFrame(id, attempt, rgba, width, height))
      .catch((error) => {
        console.log(`Attempt ${attempt} frame ${id}: error while adding it in the queue: \n ${error}`);
      });
  }

  extractS3FileURL(postURL, uploadPath) {
    const url = new URL(postURL);
    const subdomain = url.hostname.split(".")[0];
    const s3FileURL = `s3://${subdomain}/${uploadPath}`;
    return s3FileURL;
  }

  async uploadFrame(id, attempt, rgba, width, height) {
    // Only attempt to fetch frame upload data one time.
    // If it fails we will not upload any frames and not try again to minimize impact on UX.
    // In case the frameUpload data could not be successfully fetched, we don't proceed with
    if (this.frameUploadData === null) {
      if (this.attemptedFetchFrameUploadData) {
        return Promise.reject(new Error(""));
      } else {
        await this.fetchFrameUploadData();
      }
    }

    let frameIDString = id.toString().padStart(6, "0");
    let s3FilePath = `${this.frameUploadData["upload_path"]}${this.sessionId}/${attempt}/${frameIDString}.jpg`;
    let postDataURL = this.frameUploadData["post_data"]["url"];
    let s3URL = this.extractS3FileURL(postDataURL, s3FilePath);
    let frameUploadFields = this.frameUploadData["post_data"]["fields"];
    this.inputFramesURLs.push(s3URL);

    return new Promise((resolve, reject) => {
      this.frameUploadWorker.postMessage({ id, attempt, s3FilePath, frameUploadFields, postDataURL, rgba, width, height });

      this.frameUploadWorker.onmessage = (event) => {
        if (event.data.s3FilePath === s3FilePath) {
          event.data.success ? resolve(event.data.result) : reject(event.data.error);
        } else {
          reject();
        }
      };

      this.frameUploadWorker.onerror = (event) => {
        reject("Worker error");
      };
    });
  }

  getFramesQueue() {
    return this.inputFramesQueue ?? undefined;
  }

  async allFramesUploaded() {
    // Await sending of all the frames used:
    console.log("Awaiting sending of all processed frames to backend");
    let q = this.getFramesQueue();
    await q.allSettled();
    console.log("All frames sent.");
  }

  async allLogsUploaded() {
    // Await logScanData promise
    if (this.logPromise !== null) {
      await this.logPromise;
      this.logPromise = null;
    }
  }
}

export default DataLogger;
